import { DashboardLayout } from "components/DashboardLayout";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import * as PRODUCT_ONBOARDING from "constants/productOnboardings";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageTitle } from "hooks/usePageTitle";
import { useDispatch } from "react-redux";
import FinancialClosingHome from "static/images/FinancialClosing-Home.svg";
import { useGetOnboardingProductsQuery } from "store/apis/productOnboarding";
import { openOnboardingWidget } from "store/slices/onboardingWidget";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { FinancialClosingListAction } from "components/FinancialClosing/FinancialClosingListAction";
import { VarianceReportActions } from "components/FinancialClosing/VarianceReportActions";
import { FinancialClosingDetail } from "store/apis/financialClosing";
import { TableUI } from "components/design/TableUI";
import { Dash } from "components/icons/Dash";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { ComponentProps, useMemo } from "react";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import classNames from "classnames";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Info } from "components/icons/Info";
import { FileIcon } from "components/design/FileIcon";
import { FinancialClosingTableRow } from "./FinancialClosingTableRow";
import { useModal } from "hooks/useModal";
import {
  useAddFinancialClosingMutation,
  useGetAllFinancialClosingQuery,
} from "store/apis/financialClosing";
import { EmptyScreen } from "../EmptyScreen";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import EmptyCalender from "static/images/EmptyCalendar.svg";
import { FileObject } from "types/Models/fileObject";
import { FinancialClosingDetails } from "./FinancialClosingDetails";
import { VarianceReportPage } from "./VarianceReportPage";
import { LinkIcon } from "components/icons/LinkIcon";
import {
  CLOSING_DOCUMENT_REVIEW_STATUS,
  CLOSING_DOCUMENT_REVIEW_STATUS_TYPE,
  CLOSING_STATUS,
} from "constants/financialClosing";
import { FinancialsModal } from "./FinancialsModal";
import { DISPLAY_DATE } from "constants/date";
import { closeFloatingChat } from "store/slices/chat";
import { useUpdateQuery } from "hooks/useQuery";
import { FinancialReviewModal } from "./FinancialReview/FinancialReviewModal";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { ACCOUNTING_METHODS } from "constants/bookkeeping";

export const Period = ({
  end_date,
  start_date,
}: {
  start_date: string;
  end_date: string;
}) => {
  const startDate = dayjs(start_date);
  const endDate = dayjs(end_date);
  const isSameYear = startDate.year() === endDate.year();
  const isSameMonth =
    startDate.month() === endDate.month() &&
    startDate.year() === endDate.year();

  if (isSameMonth) {
    return startDate.format("MMM YYYY");
  }
  if (isSameYear) {
    return `${startDate.format("MMM")}-${endDate.format("MMM YYYY")}`;
  }
  return `${startDate.format("MMM YYYY")}-${endDate.format("MMM YYYY")}`;
};

export const TAG_TYPE: Record<string, ComponentProps<typeof Tag>["tagType"]> = {
  [CLOSING_STATUS.REVIEW_PENDING]: "orange",
  [CLOSING_STATUS.IN_PROGRESS]: "blue",
  [CLOSING_STATUS.COMPLETED]: "green",
  [CLOSING_STATUS.REQUEST_CHANGES]: "red",
  [CLOSING_STATUS.AMENDED]: "gray",
  [CLOSING_STATUS.PENDING_INFO]: "yellow",
  [CLOSING_DOCUMENT_REVIEW_STATUS.NOT_REVIEWED]: "gray",
  [CLOSING_DOCUMENT_REVIEW_STATUS.REVIEWED]: "green",
};

export const TAG_LABEL: Record<string, string> = {
  [CLOSING_STATUS.REVIEW_PENDING]: "Review Pending",
  [CLOSING_STATUS.IN_PROGRESS]: "Ongoing",
  [CLOSING_STATUS.COMPLETED]: "Completed",
  [CLOSING_STATUS.REQUEST_CHANGES]: "Changes Requested",
  [CLOSING_STATUS.AMENDED]: "Amended",
  [CLOSING_STATUS.PENDING_INFO]: "Pending Info",
  [CLOSING_DOCUMENT_REVIEW_STATUS.NOT_REVIEWED]: "Not Reviewed",
  [CLOSING_DOCUMENT_REVIEW_STATUS.REVIEWED]: "Reviewed",
};

const AMENDMENT_IN_PROGRESS_LABEL = "Amendment in progress";
const AMENDMENT_IN_PROGRESS_TOOLTIP =
  "Amending this closing because changes are requested. New financial closing will be shared by your bookkeeper soon.";

export const FinancialStatus = ({
  status,
  isAmendmentInProgress = false,
}: {
  status:
    | FinancialClosingDetail["status"]
    | CLOSING_DOCUMENT_REVIEW_STATUS_TYPE;
  isAmendmentInProgress?: boolean;
}) => {
  if (isAmendmentInProgress) {
    return (
      <ConditionalToolTip
        condition={
          <div className="t-w-52 t-text-start">
            {AMENDMENT_IN_PROGRESS_TOOLTIP}
          </div>
        }
      >
        <span className="t-inline-flex">
          <Tag tagType="gray" icon={false} rounded>
            <span className="t-flex t-items-center t-gap-1">
              {AMENDMENT_IN_PROGRESS_LABEL}
              <Info size="14" />
            </span>
          </Tag>
        </span>
      </ConditionalToolTip>
    );
  }

  return (
    <Tag tagType={TAG_TYPE[status]} icon={false} rounded>
      {TAG_LABEL[status]}
    </Tag>
  );
};

const Action = ({
  financialClosingItem,
  onView,
  entityId,
}: {
  financialClosingItem: FinancialClosingDetail;
  onView: (uuid: string) => void;
  entityId: string;
}) => {
  const reviewModal = useModal();
  const { isCustomer } = useRoleBasedView();
  const { status } = financialClosingItem;

  const showViewforAdmin =
    !isCustomer && status === CLOSING_STATUS.REVIEW_PENDING;

  const showViewForCustomer =
    isCustomer && status === CLOSING_STATUS.REQUEST_CHANGES;

  const showReviewChanges =
    !isCustomer && status === CLOSING_STATUS.REQUEST_CHANGES;

  const showAddDetails =
    status === CLOSING_STATUS.IN_PROGRESS ||
    status === CLOSING_STATUS.PENDING_INFO;

  const showReviewForCustomer =
    status === CLOSING_STATUS.REVIEW_PENDING && isCustomer;

  if (showReviewForCustomer) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="all:unset"
      >
        <Button customType="primary" size="small" onClick={reviewModal.open}>
          <span className="t-w-16 t-flex t-justify-center t-items-center">
            Review
          </span>
        </Button>
        <FinancialReviewModal
          isOpen={reviewModal.isOpen}
          close={reviewModal.close}
          financialClosingId={financialClosingItem.uuid}
          entityId={entityId}
        />
      </button>
    );
  }

  if (
    status === CLOSING_STATUS.COMPLETED ||
    status === CLOSING_STATUS.AMENDED ||
    showViewforAdmin ||
    showViewForCustomer
  ) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="all:unset"
      >
        <Button customType="secondary" size="small" onClick={reviewModal.open}>
          <span className="t-w-16 t-flex t-justify-center t-items-center">
            View
          </span>
        </Button>
        <FinancialReviewModal
          isOpen={reviewModal.isOpen}
          close={reviewModal.close}
          financialClosingId={financialClosingItem.uuid}
          entityId={entityId}
        />
      </button>
    );
  }

  if (showReviewChanges) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="all:unset"
      >
        <Button customType="primary" size="small" onClick={reviewModal.open}>
          <span className="t-min-w-16 t-whitespace-nowrap">Review changes</span>
        </Button>
        <FinancialReviewModal
          isOpen={reviewModal.isOpen}
          close={reviewModal.close}
          financialClosingId={financialClosingItem.uuid}
          entityId={entityId}
        />
      </button>
    );
  }

  if (showAddDetails) {
    return (
      <LockedUi
        feature={WRITE_PERMISSION_KEYS.FINANCIALS}
        blockedUi={<LockedButton text="Add details" />}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onView(financialClosingItem.uuid);
          }}
          className="all:unset"
        >
          <Button customType="primary" size="small">
            <span className="t-min-w-16 t-whitespace-nowrap">Add details</span>
          </Button>
        </button>
      </LockedUi>
    );
  }

  return (
    <div className="t-text-text-30">
      <Dash />
    </div>
  );
};

const Financial = ({
  file,
  link,
}: {
  file: FileObject | null;
  link: string | null;
}) => {
  if (!file && !link) return null;

  if (file) {
    return (
      <Button type="button" customType="ghost_icon" size="xsmall">
        <FileIcon fileType={file?.file_type} size="20" />
      </Button>
    );
  }

  return (
    <>
      {link && (
        <Button type="button" customType="ghost_icon" size="xsmall">
          <LinkIcon />
        </Button>
      )}
    </>
  );
};

const BooksOnboarding = () => {
  const dispatch = useDispatch();
  const entityId = useCurrentEntityId();
  const { update } = useUpdateQuery();

  const openBooksOnboarding = () => {
    dispatch(
      openOnboardingWidget({ product: PRODUCT_ONBOARDING.BOOKS, entityId })
    );
    update({ query: "onboarding_widget", value: "open" });
  };

  return (
    <div className="t-w-full t-h-full t-flex t-flex-col t-gap-12 t-justify-center">
      <div className="t-flex t-justify-center t-w-full t-h-[20%]">
        <img src={FinancialClosingHome} alt="Financials" />
      </div>
      <div className="t-flex t-justify-center">
        <div className="t-flex t-flex-col t-gap-4 t-justify-center t-items-center">
          <div className="t-text-subtitle-sm">Financial Closing</div>
          <div className="t-text-body-sm t-text-text-30 t-w-[60%] t-flex t-flex-wrap t-justify-around t-items-center t-text-center">
            Complete onboarding to view and track updates of your bookkeeping
            process.
          </div>
          <Button
            customType="primary"
            size="small"
            onClick={openBooksOnboarding}
          >
            Complete onboarding
          </Button>
        </div>
      </div>
    </div>
  );
};

const Financials = ({
  financialClosing,
}: {
  financialClosing: FinancialClosingDetail;
}) => {
  const financialsModal = useModal();
  const {
    cash_flow_statement,
    balance_sheet,
    income_statement,
    cash_flow_statement_url,
    balance_sheet_url,
    income_statement_url,
    other_financials,
  } = financialClosing;

  const noFinancials =
    !cash_flow_statement &&
    !balance_sheet &&
    !income_statement &&
    !cash_flow_statement_url &&
    !balance_sheet_url &&
    !income_statement_url;

  if (noFinancials && other_financials.length === 0) {
    return (
      <div className="t-text-text-30">
        <Dash />
      </div>
    );
  }

  if (noFinancials && other_financials.length) {
    return (
      <>
        <button onClick={financialsModal.open} className="all:unset">
          <Financial file={other_financials[0]} link={null} />
        </button>
        <FinancialsModal
          {...financialsModal}
          financialClosing={financialClosing}
        />
      </>
    );
  }

  return (
    <>
      <button
        className="all:unset t-flex t-gap-2 t-items-center"
        onClick={financialsModal.open}
      >
        <Financial file={balance_sheet} link={balance_sheet_url} />
        <Financial file={income_statement} link={income_statement_url} />
        <Financial file={cash_flow_statement} link={cash_flow_statement_url} />

        {other_financials.length !== 0 && (
          <div className="t-bg-neutral-0 t-rounded t-text-body-sm t-text-text-30 t-p-2">
            +{other_financials.length}
          </div>
        )}
      </button>
      <FinancialsModal
        {...financialsModal}
        financialClosing={financialClosing}
      />
    </>
  );
};

const PeriodColumn = ({
  financialClosing,
  isChild,
  hasEarlierVersions,
}: {
  financialClosing: FinancialClosingDetail;
  isChild: boolean;
  hasEarlierVersions: boolean;
}) => {
  const isVersioned = isChild || hasEarlierVersions;

  return (
    <button
      className={classNames("all:unset t-flex t-flex-col t-gap-0.5 t-group", {
        "t-pl-7": isChild,
      })}
    >
      <div className="t-flex t-items-center t-gap-2 t-text-subtitle-sm t-text-text-60 group-hover:t-text-purple-80">
        <Period
          end_date={financialClosing.end_date}
          start_date={financialClosing.start_date}
        />
        {isVersioned && (
          <Tag tagType="gray" icon={false} size="small">
            <span className="!t-text-body-sm-italic !t-italic">
              V{financialClosing.version}
            </span>
          </Tag>
        )}
      </div>
      <div className="t-text-caption t-text-text-30 group-hover:t-text-purple-80">
        {financialClosing.accounting_method} METHOD
      </div>
    </button>
  );
};

const toCustomerVisibleClosing = (
  closing: FinancialClosingDetail
): FinancialClosingDetail => {
  const [previousVersion, ...olderVersions] = closing.earlier_versions ?? [];
  const isNewVersionNotSent =
    closing.version > 1 &&
    (closing.status === CLOSING_STATUS.IN_PROGRESS ||
      closing.status === CLOSING_STATUS.PENDING_INFO);

  if (!isNewVersionNotSent || !previousVersion) {
    return closing;
  }

  return {
    ...previousVersion,
    earlier_versions: olderVersions,
    earlier_versions_count: olderVersions.length,
    original_sent_at: closing.original_sent_at,
  };
};

export const FinancialClosing = () => {
  usePageTitle("Financial Closing");
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const { alertToast } = useToast();
  const { search } = history.location;
  const dispatch = useDispatch();
  const { isCustomer } = useRoleBasedView();

  const {
    data: products,
    isLoading,
    isSuccess,
  } = useGetOnboardingProductsQuery(
    {
      groupId: groupId!,
    },
    { skip: !groupId }
  );

  const platformOnboarding = products?.find(
    (p) => p.name === PRODUCT_ONBOARDING.BOOKS
  );

  const isBooksOnboardingComplete =
    platformOnboarding && platformOnboarding.status === "COMPLETED";

  const [addFinancial, { isLoading: isAdding }] =
    useAddFinancialClosingMutation();

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const goToDetails = (uuid: string) => {
    history.push(
      link(`/${uuid}`, {
        includeUrl: true,
      })
    );
  };

  const onAddFinancial = async () => {
    try {
      const { uuid } = await addFinancial({
        entityId,
        payload: {
          accounting_method:
            bookkeepingSettings?.accounting_method || ACCOUNTING_METHODS.CASH,
          start_date: dayjs().startOf("month").format(YYYY_MM_DD),
          end_date: dayjs().endOf("month").format(YYYY_MM_DD),
        },
      }).unwrap();
      goToDetails(uuid);
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const { data: financialClosings = [], ...financialClosingsStates } =
    useGetAllFinancialClosingQuery(
      { entityId },
      {
        skip: !entityId,
      }
    );

  const isEmpty = financialClosings.length === 0;
  const createColumn = createColumnHelper<FinancialClosingDetail>();

  const invoiceColumns = useMemo(
    () => [
      createColumn.accessor("period", {
        header: "PERIOD",
        size: 25,
        cell: (info) => {
          return (
            <PeriodColumn
              financialClosing={info.row.original}
              isChild={info.row.depth > 0}
              hasEarlierVersions={info.row.subRows.length > 0}
            />
          );
        },
      }),

      createColumn.accessor("status", {
        header: "CLOSING STATUS",
        size: 20,
        cell: (info) => {
          const { status } = info.row.original;
          const isAmendmentInProgress =
            isCustomer &&
            info.row.depth === 0 &&
            status === CLOSING_STATUS.AMENDED;

          return (
            <FinancialStatus
              status={status}
              isAmendmentInProgress={isAmendmentInProgress}
            />
          );
        },
      }),
      createColumn.accessor("creator", {
        header: "Created by",
        size: 20,

        cell: (info) => {
          const creatorName = info.row.original.creator;
          if (!creatorName) {
            return <>-</>;
          }
          return (
            <div className="t-flex t-flex-col">
              <div className="t-text-subtext-sm t-text-text-60">
                {dayjs(creatorName.created_at).format(DISPLAY_DATE)}
              </div>
              <div className="t-text-caption t-text-text-30">
                {creatorName.name}
              </div>
            </div>
          );
        },
      }),
      createColumn.accessor("reviewer", {
        header: "Reviewed by",
        size: 20,
        cell: (info) => {
          const originalValues = info.row.original;
          if (
            !originalValues ||
            !originalValues.reviewer ||
            !originalValues.reviewer.name
          ) {
            return <>-</>;
          }
          return (
            <div className="t-flex t-flex-col">
              <div className="t-text-subtext-sm t-text-text-60">
                {originalValues.first_completed_at
                  ? dayjs(originalValues.first_completed_at).format(
                      DISPLAY_DATE
                    )
                  : originalValues.reviewer?.reviewed_at
                  ? dayjs(originalValues.reviewer.reviewed_at).format(
                      DISPLAY_DATE
                    )
                  : "-"}
              </div>
              <div className="t-text-caption t-text-text-30">
                {originalValues.reviewer.name}
              </div>
            </div>
          );
        },
      }),

      createColumn.accessor("uuid", {
        header: "FINANCIALS",
        size: 20,
        cell: (info) => {
          return (
            <button onClick={(e) => e.stopPropagation()} className="all:unset">
              <Financials financialClosing={info.row.original} />
            </button>
          );
        },
      }),

      createColumn.accessor("variance_report_status", {
        header: "FLUX",
        size: 20,
        cell: (info) => (
          <VarianceReportActions financialClosingItem={info.row.original} />
        ),
      }),

      createColumn.accessor("status", {
        header: "ACTION",
        size: 15,
        cell: (info) => {
          return (
            <div className="t-flex t-w-full t-justify-center t-items-center">
              <Action
                financialClosingItem={info.row.original}
                onView={goToDetails}
                entityId={entityId || ""}
              />
            </div>
          );
        },
      }),

      createColumn.accessor("uuid", {
        header: "",
        size: 5,
        cell: (info) => {
          const financialClosingItem = info.row.original;
          return (
            <FinancialClosingListAction
              financialClosingItem={financialClosingItem}
            />
          );
        },
      }),
    ],
    [entityId, isCustomer]
  );

  const tableData = useMemo(
    () =>
      isCustomer
        ? financialClosings.map(toCustomerVisibleClosing)
        : financialClosings,
    [isCustomer, financialClosings]
  );

  const table = useReactTable({
    data: tableData,
    columns: invoiceColumns,
    getCoreRowModel: getCoreRowModel(),
    getSubRows: (row) => row.earlier_versions,
    getExpandedRowModel: getExpandedRowModel(),
    defaultColumn: {
      minSize: 5,
    },
  });

  const handleRowClick = (row: Row<FinancialClosingDetail>) => {
    dispatch(closeFloatingChat());
    goToDetails(row.original.uuid);
  };

  const { url } = useRouteMatch();

  return (
    <>
      <Switch>
        <Route exact path={`${url}/:financialClosingId/variance-report`}>
          <VarianceReportPage listLink={`${url}${search}`} />
        </Route>
        <Route exact path={url}>
          <Async.Root
            {...{ isLoading, isEmpty: !isBooksOnboardingComplete, isSuccess }}
          >
            <Async.Empty>
              <DashboardLayout header={<Header v2 title="Financial Closing" />}>
                <div className="t-flex t-flex-col t-gap-6 t-px-5 t-pb-1 t-h-full">
                  <BooksOnboarding />
                </div>
              </DashboardLayout>
            </Async.Empty>
            <Async.Success>
              <DashboardLayout
                header={
                  <Header
                    v2
                    title="Financial Closing"
                    right={
                      <>
                        {!isEmpty && (
                          <LockedUi
                            feature={WRITE_PERMISSION_KEYS.FINANCIALS}
                            blockedUi={<LockedButton text="Add financials" />}
                          >
                            <Button
                              customType="primary"
                              size="small"
                              onClick={onAddFinancial}
                              disabled={isAdding}
                              isLoading={isAdding}
                            >
                              Add financials
                            </Button>
                          </LockedUi>
                        )}
                      </>
                    }
                  />
                }
              >
                <Async.Root
                  isEmpty={isEmpty}
                  isLoading={financialClosingsStates.isLoading}
                  isSuccess={financialClosingsStates.isSuccess}
                >
                  <Async.Empty>
                    <EmptyScreen
                      className="t-h-[calc(100%-64px)]"
                      text={
                        <span className="t-text-body-sm t-text-text-30">
                          Add financials and close tasks periodically for
                          accurate accounting
                        </span>
                      }
                      cta={
                        <LockedUi
                          feature={WRITE_PERMISSION_KEYS.FINANCIALS}
                          blockedUi={<LockedButton text="Add financials" />}
                        >
                          <Button
                            customType="primary"
                            size="small"
                            onClick={onAddFinancial}
                            disabled={isAdding}
                            isLoading={isAdding}
                          >
                            Add financials
                          </Button>
                        </LockedUi>
                      }
                    >
                      <img src={EmptyCalender} alt="EmptyCalender" />
                    </EmptyScreen>
                  </Async.Empty>
                  <Async.Success>
                    <TableUI
                      table={table}
                      contentClassName="sm:t-min-w-[900px]"
                    >
                      {({ row }) => (
                        <FinancialClosingTableRow
                          key={row.id}
                          row={row}
                          onRowClick={handleRowClick}
                        />
                      )}
                    </TableUI>
                  </Async.Success>
                </Async.Root>
              </DashboardLayout>
            </Async.Success>
          </Async.Root>
        </Route>
      </Switch>
      <Switch>
        <Route exact path={`${url}/:financialClosingId`}>
          <FinancialClosingDetails
            close={() => {
              history.push(`${url}${search}`);
            }}
            breadcrumbs={[
              {
                name: "Financial closing",
                link: `${url}${search}`,
              },
            ]}
          />
        </Route>
      </Switch>
    </>
  );
};
